import { useMemo, useState } from 'react'
import { Table } from '@radix-ui/themes'
import { Text } from '@radix-ui/themes'
import { IconDotsVertical } from '@tabler/icons-react'
import { QuestionDetail, CaseStudyQuestion } from '@/types/question'
import { cn, truncate } from '@/lib/utils'
import { getMarksPercentage, getMarksStyle } from '@/utils/style'
import {
  Accordion,
  AccordionContent,
  AccordionItem,
  AccordionTrigger,
} from '@/components/ui/accordion'
import Hstack from '@/components/ui/hstack'
import Stack from '@/components/ui/stack'
import {
  Table as UiTable,
  TableHeader as UiTableHeader,
  TableBody as UiTableBody,
  TableRow as UiTableRow,
  TableCell as UiTableCell,
  TableHead as UiTableHead,
} from '@/components/ui/table'
import DataTable from '@/components/ui/table/data-table'
import QuestionDrawer from '@/features/question-lib/components/QuestionDrawer'
import { TypeValue } from '@/features/question-lib/constant'
import { Button } from '../button'
import {
  DropdownMenu,
  DropdownMenuContent,
  DropdownMenuItem,
  DropdownMenuTrigger,
} from '../dropdown-menu'
import TablePaginate from '../table-paginate'

interface CaseStudyGroupView {
  caseStudyId: number
  items: CaseStudyQuestion[]
  totalTime: number
  totalMarks: number
  selectedCount: number
}

type SubQ = number | 'N/A'

interface QuestionRow {
  questionId: number
  moodleQuestionId: string
  question: string
  subQuestion: SubQ
  marks: number
  chapterRelated: string
  time: number
  avg_ai_marks?: number
  avg_tutor_marks?: number
}

interface SectionTableViewProps {
  title: string
  totals?: {
    totalQuestions: number
    totalMarks: number
    totalTime: number
  }
  nonCaseQuestions?: QuestionDetail[]
  caseStudyGroups?: CaseStudyGroupView[]
  page: number
  totalPages: number
  onPageChange: (page: number) => void
  isLoading?: boolean
}

function getRowFromQuestion(item: QuestionDetail): QuestionRow {
  const subQuestions = item?.sub_questions || []
  const marks = subQuestions.reduce((sum, sq) => sum + (sq?.marks || 0), 0)
  const time = subQuestions.reduce((sum, sq) => sum + (sq?.time_limit || 0), 0)
  const subQuestion: SubQ =
    subQuestions.length === 0 ? 'N/A' : subQuestions.length
  let chapterList: string[] | undefined = Array.isArray(item.chapters)
    ? item.chapters
    : undefined
  if (
    (!chapterList || chapterList.length === 0) &&
    Array.isArray(subQuestions)
  ) {
    const set = new Set<string>()
    for (const sq of subQuestions) {
      if (Array.isArray((sq as any).chapters)) {
        for (const name of (sq as any).chapters as string[])
          if (name) set.add(name)
      }
    }
    chapterList = Array.from(set)
  }
  const chapterRelated =
    chapterList && chapterList.length
      ? chapterList.join(', ')
      : item?.chapter || ''
  return {
    questionId: item.question_id,
    moodleQuestionId: item.moodle_question_id,
    question: item?.text || item?.question_text || '',
    subQuestion,
    marks,
    time,
    chapterRelated,
    avg_ai_marks: item?.avg_ai_marks,
    avg_tutor_marks: item?.avg_tutor_marks,
  }
}

function computeTotals(items: QuestionDetail[] | undefined): {
  totalQuestions: number
  totalMarks: number
  totalTime: number
} {
  if (!items?.length) return { totalQuestions: 0, totalMarks: 0, totalTime: 0 }
  let totalMarks = 0
  let totalTime = 0
  for (const q of items) {
    const subs = q?.sub_questions || []
    for (const sq of subs) {
      totalMarks += sq?.marks || 0
      totalTime += sq?.time_limit || 0
    }
  }
  return { totalQuestions: items.length, totalMarks, totalTime }
}

function computeCaseStudyTotals(groups: CaseStudyGroupView[] | undefined) {
  if (!groups?.length) return { totalQuestions: 0, totalMarks: 0, totalTime: 0 }
  let totalQuestions = 0
  let totalMarks = 0
  let totalTime = 0
  for (const g of groups) {
    totalQuestions += g?.selectedCount || 0
    totalMarks += g?.totalMarks || 0
    totalTime += g?.totalTime || 0
  }
  return { totalQuestions, totalMarks, totalTime }
}

export default function SectionTableView({
  title,
  totals: _totals,
  nonCaseQuestions,
  caseStudyGroups,
  page,
  totalPages,
  onPageChange,
  isLoading,
}: SectionTableViewProps) {
  const rows: QuestionRow[] = useMemo(
    () => (nonCaseQuestions || []).map(getRowFromQuestion),
    [nonCaseQuestions]
  )

  const nonCase = useMemo(
    () => computeTotals(nonCaseQuestions),
    [nonCaseQuestions]
  )
  const cs = useMemo(
    () => computeCaseStudyTotals(caseStudyGroups),
    [caseStudyGroups]
  )

  const totalQuestions =
    _totals?.totalQuestions ?? nonCase.totalQuestions + cs.totalQuestions
  const totalMarks = _totals?.totalMarks ?? nonCase.totalMarks + cs.totalMarks
  const totalTime = _totals?.totalTime ?? nonCase.totalTime + cs.totalTime

  const [quesntionId, setQuesionId] = useState<number>(0)
  // Tells the drawer which endpoint to ask; set on every View, never left over.
  const [typeValue, setTypeValue] = useState<string>('')

  // Column width mapping to keep Non-casestudy and Case Study tables aligned
  const COL = {
    id: 'w-[6%] min-w-[6%] max-w-[6%]',
    chapter: 'w-[20%] min-w-[20%] max-w-[20%]',
    question: 'w-[15%] min-w-[15%] max-w-[15%]',
    sub: 'w-[10%] min-w-[10%] max-w-[10%]',
    time: 'w-[102px] min-w-[102px]',
    marks: 'w-[102px] min-w-[102px]',
    ai: 'w-[10%] min-w-[10%] max-w-[10%]',
    tutor: 'w-[10%] min-w-[10%] max-w-[10%]',
    action: 'w-[60px] min-w-[60px] max-w-[60px]',
  }

  return (
    <>
      <Stack gap={'12px'}>
        <Hstack justify={'between'} align={'center'}>
          <Stack gap={'0'}>
            <Text className='text-[24px] font-medium text-[#002652]'>
              Section {title}
            </Text>
            <Hstack gap={'32px'}>
              <Text className='text-[16px] font-normal text-[#1D2939]'>
                Total Questions:{' '}
                <span className='text-[16px] font-medium text-[#101828]'>
                  {totalQuestions}
                </span>
              </Text>
              <Text className='text-[16px] font-normal text-[#1D2939]'>
                Duration:{' '}
                <span className='text-[16px] font-medium text-[#101828]'>
                  {totalTime} minutes
                </span>
              </Text>
              <Text className='text-[16px] font-normal text-[#1D2939]'>
                Total Marks Available:{' '}
                <span className='text-[16px] font-medium text-[#101828]'>
                  {totalMarks}
                </span>
              </Text>
            </Hstack>
          </Stack>
        </Hstack>

        {isLoading || rows.length > 0 ? (
          <Stack gap={'16px'} pb={'20px'} className='rounded-[16px]'>
            <Text className='text-[16px] font-normal text-[#1D2939]'>
              Non-Case Study Questions
            </Text>
            <DataTable
              mx={'0'}
              bgHeader='#ffffff'
              showFirstDivider
              showLastDivider
              className='rounded-md border bg-white'
            >
              {
                <>
                  <Table.ColumnHeaderCell
                    className={`truncate !font-normal text-[#002652] ${COL.id}`}
                  >
                    Question ID
                  </Table.ColumnHeaderCell>
                  <Table.ColumnHeaderCell
                    className={`!font-medium text-[#002652] ${COL.chapter}`}
                  >
                    Chapter
                  </Table.ColumnHeaderCell>
                  <Table.ColumnHeaderCell
                    className={`!font-medium text-[#002652] ${COL.question}`}
                  >
                    Question
                  </Table.ColumnHeaderCell>
                  <Table.ColumnHeaderCell
                    className={`text-center !font-medium text-[#002652] ${COL.sub}`}
                  >
                    Sub-question
                  </Table.ColumnHeaderCell>
                  <Table.ColumnHeaderCell
                    className={`text-center !font-medium text-[#002652] ${COL.time}`}
                  >
                    Time
                  </Table.ColumnHeaderCell>
                  <Table.ColumnHeaderCell
                    className={`text-center !font-medium text-[#002652] ${COL.marks}`}
                  >
                    Marks Available
                  </Table.ColumnHeaderCell>
                  <Table.ColumnHeaderCell
                    className={`text-center !font-medium text-[#002652] ${COL.ai}`}
                  >
                    Average AI Mark
                  </Table.ColumnHeaderCell>
                  <Table.ColumnHeaderCell
                    className={`text-center !font-medium text-[#002652] ${COL.tutor}`}
                  >
                    Average Tutor Mark
                  </Table.ColumnHeaderCell>
                  <Table.ColumnHeaderCell
                    className={`text-center !font-medium text-[#002652] ${COL.action}`}
                  ></Table.ColumnHeaderCell>
                </>
              }
              {
                <>
                  {isLoading ? (
                    <Table.Row>
                      <Table.Cell colSpan={9} className='h-60'>
                        <div className='flex h-full w-full items-center justify-center'>
                          <div className='h-10 w-10 animate-spin rounded-full border-4 border-[#002652] border-t-transparent'></div>
                        </div>
                      </Table.Cell>
                    </Table.Row>
                  ) : (
                    rows.map((item, index) => (
                      <Table.Row key={index}>
                        <Table.Cell className={`font-normal ${COL.id}`}>
                          {item.moodleQuestionId}
                        </Table.Cell>
                        <Table.Cell
                          className={`truncate font-normal ${COL.chapter}`}
                        >
                          {item.chapterRelated}
                        </Table.Cell>
                        <Table.Cell className={`font-normal ${COL.question}`}>
                          {truncate(item.question, 30)}
                        </Table.Cell>
                        <Table.Cell
                          className={`text-center font-normal ${COL.sub}`}
                        >
                          {item.subQuestion}
                        </Table.Cell>
                        <Table.Cell
                          className={`text-center font-normal ${COL.time}`}
                        >
                          {item.time}
                        </Table.Cell>
                        <Table.Cell
                          className={`text-center font-normal ${COL.marks}`}
                        >
                          {item.marks}
                        </Table.Cell>
                        <Table.Cell
                          className={cn(
                            `text-center font-normal ${COL.ai}`,
                            getMarksStyle(item?.avg_ai_marks)
                          )}
                        >
                          {getMarksPercentage(item?.avg_ai_marks)}
                        </Table.Cell>
                        <Table.Cell
                          className={cn(
                            `text-center font-normal ${COL.tutor}`,
                            getMarksStyle(item?.avg_tutor_marks)
                          )}
                        >
                          {getMarksPercentage(item?.avg_tutor_marks)}
                        </Table.Cell>
                        <Table.Cell className={`font-normal ${COL.action}`}>
                          <DropdownMenu>
                            <DropdownMenuTrigger asChild>
                              <Button variant='ghost' className='h-8 w-8 p-0'>
                                <span className='sr-only'>Open menu</span>
                                <IconDotsVertical />
                              </Button>
                            </DropdownMenuTrigger>
                            <DropdownMenuContent align='end'>
                              <DropdownMenuItem
                                className='cursor-pointer'
                                onClick={() => {
                                  setTypeValue('')
                                  setQuesionId(item.questionId)
                                }}
                              >
                                View
                              </DropdownMenuItem>
                            </DropdownMenuContent>
                          </DropdownMenu>
                        </Table.Cell>
                      </Table.Row>
                    ))
                  )}
                </>
              }
            </DataTable>
            <TablePaginate
              className='justify-end pr-[24px]'
              page={page}
              totalPages={totalPages}
              onPageChange={onPageChange}
            />
          </Stack>
        ) : (
          <></>
        )}

        {!caseStudyGroups?.length ? (
          <></>
        ) : (
          <Stack gap={'16px'} className='rounded-[16px]'>
            <Hstack justify={'start'}>
              <Text className='text-[16px] font-normal text-[#1D2939]'>
                Case Study Questions
              </Text>
            </Hstack>
            <Accordion type='multiple' className='rounded-md border bg-white'>
              {(caseStudyGroups || []).map((group) => (
                <AccordionItem
                  key={`sel-cs-${group.caseStudyId}`}
                  value={`sel-cs-${group.caseStudyId}`}
                >
                  <AccordionTrigger className='px-4'>
                    <Hstack justify={'between'} className='w-full'>
                      <Hstack gap={'8px'}>
                        <Text className='text-[14px] font-medium text-[#002652]'>
                          Case Study {group.caseStudyId}
                        </Text>
                        <Text className='text-[14px] text-[#475467]'>
                          ({group.selectedCount} Question)
                        </Text>
                      </Hstack>
                      <Hstack gap={'24px'}>
                        <Text className='text-[14px] text-[#1D2939]'>
                          Duration:{' '}
                          <span className='font-medium'>
                            {group.totalTime} minutes
                          </span>
                        </Text>
                        <Text className='text-[14px] text-[#1D2939]'>
                          Total Marks Available:{' '}
                          <span className='font-medium'>
                            {group.totalMarks}
                          </span>
                        </Text>
                      </Hstack>
                    </Hstack>
                  </AccordionTrigger>
                  <AccordionContent>
                    <div className='overflow-hidden'>
                      <UiTable className='border-b'>
                        <UiTableHeader>
                          <UiTableRow>
                            <UiTableHead
                              className={`px-6 py-1.5 text-[#002652] ${COL.id}`}
                            >
                              Question ID
                            </UiTableHead>
                            <UiTableHead
                              className={`px-6 py-1.5 text-[#002652] ${COL.chapter}`}
                            >
                              Chapter
                            </UiTableHead>
                            <UiTableHead
                              className={`px-6 py-1.5 text-[#002652] ${COL.question}`}
                            >
                              Question
                            </UiTableHead>
                            <UiTableHead
                              className={`px-6 py-1.5 text-center text-[#002652] ${COL.sub}`}
                            >
                              Sub-question
                            </UiTableHead>
                            <UiTableHead
                              className={`px-6 py-1.5 text-center text-[#002652] ${COL.time}`}
                            >
                              Time
                            </UiTableHead>
                            <UiTableHead
                              className={`px-6 py-1.5 text-center text-[#002652] ${COL.marks}`}
                            >
                              Marks Available
                            </UiTableHead>
                            <UiTableHead
                              className={`px-6 py-1.5 text-center text-[#002652] ${COL.ai}`}
                            >
                              Average AI Mark
                            </UiTableHead>
                            <UiTableHead
                              className={`px-6 py-1.5 text-center text-[#002652] ${COL.tutor}`}
                            >
                              Average Tutor Mark
                            </UiTableHead>
                            <UiTableHead
                              className={`px-6 py-1.5 text-[#002652] ${COL.action}`}
                            ></UiTableHead>
                          </UiTableRow>
                        </UiTableHeader>
                        <UiTableBody>
                          {group.items.length ? (
                            group.items.map((q, idx) => (
                              <UiTableRow key={`g-${group.caseStudyId}-${idx}`}>
                                <UiTableCell
                                  className={`px-6 py-1.5 font-normal ${COL.id}`}
                                >
                                  {q.question_id}
                                </UiTableCell>
                                <UiTableCell
                                  className={`px-6 py-1.5 ${COL.chapter}`}
                                >
                                  {truncate(
                                    Array.isArray(q.chapter_name)
                                      ? q.chapter_name.join(', ')
                                      : String(q.chapter_name || ''),
                                    30
                                  )}
                                </UiTableCell>
                                <UiTableCell
                                  className={`px-6 py-1.5 ${COL.question}`}
                                >
                                  {truncate(q.text, 30)}
                                </UiTableCell>
                                <UiTableCell
                                  className={`px-6 py-1.5 text-center ${COL.sub}`}
                                >
                                  {q.sub_questions}
                                </UiTableCell>
                                <UiTableCell
                                  className={`px-6 py-1.5 text-center ${COL.time}`}
                                >
                                  {q.time}
                                </UiTableCell>
                                <UiTableCell
                                  className={`px-6 py-1.5 text-center ${COL.marks}`}
                                >
                                  {q.marks}
                                </UiTableCell>
                                <UiTableCell
                                  className={cn(
                                    `px-6 py-1.5 text-center ${COL.ai}`,
                                    getMarksStyle(q?.avg_ai_marks)
                                  )}
                                >
                                  {getMarksPercentage(q?.avg_ai_marks)}
                                </UiTableCell>
                                <UiTableCell
                                  className={cn(
                                    `px-6 py-1.5 text-center ${COL.tutor}`,
                                    getMarksStyle(q?.avg_tutor_marks)
                                  )}
                                >
                                  {getMarksPercentage(q?.avg_tutor_marks)}
                                </UiTableCell>
                                <Table.Cell
                                  className={`font-normal ${COL.action}`}
                                >
                                  <DropdownMenu>
                                    <DropdownMenuTrigger asChild>
                                      <Button
                                        variant='ghost'
                                        className='h-8 w-8 p-0'
                                      >
                                        <span className='sr-only'>
                                          Open menu
                                        </span>
                                        <IconDotsVertical />
                                      </Button>
                                    </DropdownMenuTrigger>
                                    <DropdownMenuContent align='end'>
                                      <DropdownMenuItem
                                        className='cursor-pointer'
                                        onClick={() => {
                                          setTypeValue(TypeValue.case_study)
                                          setQuesionId(q?.question_id)
                                        }}
                                      >
                                        View
                                      </DropdownMenuItem>
                                    </DropdownMenuContent>
                                  </DropdownMenu>
                                </Table.Cell>
                              </UiTableRow>
                            ))
                          ) : (
                            <UiTableRow>
                              <UiTableCell
                                colSpan={9}
                                className='h-24 text-center'
                              >
                                No results.
                              </UiTableCell>
                            </UiTableRow>
                          )}
                        </UiTableBody>
                      </UiTable>
                    </div>
                  </AccordionContent>
                </AccordionItem>
              ))}
            </Accordion>
          </Stack>
        )}
      </Stack>
      {!!quesntionId && (
        <QuestionDrawer
          fromChapterTable={true}
          questionId={quesntionId}
          setIsOpen={setQuesionId}
          typeValue={typeValue}
        />
      )}
    </>
  )
}
